import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import type {
    DynamicRequestBody,
    DynamicSiteResponse,
    MultipleImagesQuery,
} from './types'
import { getDynamicImage, getDynamicVideo, postDynamicData } from './services'
import { useMemo } from 'react'
import { DynamicImage } from '../../types'

export const useDynamicDataMutation = (
    successFunction?: (data: DynamicSiteResponse) => void,
) => {
    const queryClient = useQueryClient()
    return useMutation((data: DynamicRequestBody) => postDynamicData(data), {
        retry: false,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries(['activation-object'])
            successFunction && successFunction(_data)
        },
    })
}

export const useDynamicVideoQuery = (videoName?: string) => {
    const dynamicVideoQuery = useQuery(
        ['dynamic-video', videoName],
        () => getDynamicVideo(videoName),
        {
            enabled: !!videoName,
        },
    )

    return dynamicVideoQuery
}

export const useDynamicImageQuery = (imageName?: string | null) => {
    const dynamicImageQuery = useQuery(
        ['dynamicImage', imageName],
        () => getDynamicImage(imageName),
        {
            enabled: !!imageName,
        },
    )

    return dynamicImageQuery
}

export const useFetchMultipleImages = (
    images: DynamicImage[] = [],
): MultipleImagesQuery | null => {
    const queryResults = useQueries(
        images.map(({ imageName }) => ({
            queryKey: ['base64Image', imageName],
            queryFn: () => getDynamicImage(imageName),
        })),
    )

    const imagesLoading = useMemo(
        () => queryResults.some((result) => result.isLoading),
        [queryResults],
    )

    return { queryResults, imagesLoading }
}
